import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { assegnazioneCompleta } from '@eventi/assegna.ts';
import type { ProgrammaEvento } from '@eventi/tipi.ts';
import { cambiataDopoLaConferma } from '../../lib/eventi/stati';
import type { PartecipanteEvento } from '../../lib/eventi/tipi';

type Props = {
  /** Chi verra' confermato; vuoto = finestra chiusa. */
  persone: PartecipanteEvento[];
  programma: ProgrammaEvento;
  /** Se manca l'etichetta "percorso assegnato" non parte nessuna email. */
  conEmail: boolean;
  inCorso: boolean;
  onConferma: () => void;
  onChiudi: () => void;
};

const persone = (n: number) => (n === 1 ? '1 persona' : `${n} persone`);

/** Ultima domanda prima di confermare: quante persone ricevono l'email, e cosa c'e' da sapere. */
export function ConfermaInvioDialog({ persone: chi, programma, conEmail, inCorso, onConferma, onChiudi }: Props) {
  const incomplete = chi.filter((p) => !assegnazioneCompleta(programma, p.assegnazione)).length;
  const giaAvvisate = chi.filter(cambiataDopoLaConferma).length;

  return (
    <AlertDialog open={chi.length > 0} onOpenChange={(v) => !v && !inCorso && onChiudi()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confermare l'assegnazione di {persone(chi.length)}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-neutral-600">
              {conEmail ? (
                <p>
                  <strong>{persone(chi.length)}</strong>{' '}
                  {chi.length === 1
                    ? "ricevera' l'email con il percorso assegnato e lo vedra' nell'app dell'evento."
                    : "riceveranno l'email con il percorso assegnato e lo vedranno nell'app dell'evento."}
                </p>
              ) : (
                <p className="text-amber-800">
                  Nelle Impostazioni manca l'etichetta «percorso assegnato»: la conferma viene registrata ma{' '}
                  <strong>non parte nessuna email</strong>.
                </p>
              )}
              {incomplete > 0 && (
                <p className="text-amber-800">
                  {persone(incomplete)} non {incomplete === 1 ? 'ha' : 'hanno'} tutti i turni assegnati: il percorso
                  comunicato sara' incompleto.
                </p>
              )}
              {giaAvvisate > 0 && (
                <p>
                  {persone(giaAvvisate)} {giaAvvisate === 1 ? 'aveva' : 'avevano'} gia' ricevuto un percorso prima della
                  modifica: {giaAvvisate === 1 ? 'riceve' : 'ricevono'} una nuova email.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={inCorso}>Annulla</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConferma();
            }}
            disabled={inCorso}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            {inCorso && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {conEmail ? 'Conferma e invia' : 'Conferma senza email'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
